import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/*
 * Shared type-to-search dropdown used by Select, SearchableSelect and LabeledSelect.
 *
 * Unlike a native <datalist>, clicking a box that already has a value shows EVERY
 * option (the current one ticked and highlighted). The list only narrows once the
 * user starts typing.
 */

export const normalizeOptionText = (value = "") => String(value ?? "").trim().toLocaleLowerCase();

function defaultFilter(options, text) {
  const terms = normalizeOptionText(text).split(/\s+/).filter(Boolean);
  if (!terms.length) return options;
  return options.filter((option) => {
    const haystack = normalizeOptionText([option.label, option.hint, option.group].filter(Boolean).join(" "));
    return terms.every((term) => haystack.includes(term));
  });
}

export function useCombobox({ options, selectedValue, selectedLabel, onSelect, onClear, filter = defaultFilter }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(selectedLabel || "");
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    if (!open) setText(selectedLabel || "");
  }, [open, selectedLabel]);

  const visible = useMemo(() => (typed ? filter(options, text) : options), [typed, text, options, filter]);

  const openList = useCallback(() => {
    setOpen(true);
    setTyped(false);
    setActive(Math.max(0, options.findIndex((option) => option.value === selectedValue)));
  }, [options, selectedValue]);

  const pick = useCallback((option) => {
    if (!option || option.disabled) return;
    onSelect(option.value);
    setText(option.label);
    setTyped(false);
    setOpen(false);
  }, [onSelect]);

  const close = useCallback(() => {
    setOpen(false);
    setTyped(false);
  }, []);

  const inputProps = {
    value: text,
    role: "combobox",
    autoComplete: "off",
    "aria-autocomplete": "list",
    "aria-expanded": open,
    // Focus alone (e.g. a dialog focusing its first field) only selects the text;
    // a click or tap opens the full list, as do typing and the arrow keys.
    onFocus: (event) => {
      const input = event.target;
      if (input && typeof input.select === "function") input.select();
    },
    onClick: () => {
      if (!open) openList();
    },
    onChange: (event) => {
      const next = event.target.value;
      setText(next);
      setTyped(true);
      setOpen(true);
      setActive(0);
      if (!next.trim()) {
        onClear?.();
        return;
      }
      const exact = options.find((option) => !option.disabled && normalizeOptionText(option.label) === normalizeOptionText(next));
      if (exact) onSelect(exact.value);
    },
    onKeyDown: (event) => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (!open) {
          openList();
          return;
        }
        const step = event.key === "ArrowDown" ? 1 : -1;
        setActive((current) => {
          const count = visible.length;
          if (!count) return -1;
          if (current < 0) return step > 0 ? 0 : count - 1;
          return (current + step + count) % count;
        });
      } else if (event.key === "Enter") {
        if (open && visible[active]) {
          event.preventDefault();
          pick(visible[active]);
        }
      } else if (event.key === "Escape") {
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          setText(selectedLabel || "");
          close();
        }
      } else if (event.key === "Tab") {
        close();
      }
    },
    onBlur: () => {
      const exact = options.find((option) => !option.disabled && normalizeOptionText(option.label) === normalizeOptionText(text));
      if (exact && exact.value !== selectedValue) onSelect(exact.value);
      setText(exact ? exact.label : (text.trim() ? selectedLabel || "" : ""));
      close();
    },
  };

  return { open, visible, active, setActive, pick, inputProps };
}

function useAnchoredPosition(anchorRef, open) {
  const [layout, setLayout] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setLayout(null);
      return undefined;
    }
    const anchor = anchorRef.current;
    const host = anchor.closest('[role="dialog"], [role="alertdialog"]') || document.body;

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const viewportBottom = window.innerHeight || document.documentElement.clientHeight || 0;
      const hostRect = host === document.body ? null : host.getBoundingClientRect();
      const visibleBottom = hostRect ? Math.min(viewportBottom, hostRect.bottom) : viewportBottom;
      const visibleTop = hostRect ? Math.max(0, hostRect.top) : 0;
      const spaceBelow = visibleBottom - rect.bottom;
      const spaceAbove = rect.top - visibleTop;
      const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(140, Math.min(288, (openUp ? spaceAbove : spaceBelow) - 12));
      let style;
      if (!hostRect) {
        style = { position: "fixed", left: rect.left, width: rect.width, top: openUp ? rect.top - 4 : rect.bottom + 4 };
      } else {
        const left = rect.left - hostRect.left - host.clientLeft + host.scrollLeft;
        const top = (openUp ? rect.top - 4 : rect.bottom + 4) - hostRect.top - host.clientTop + host.scrollTop;
        style = { position: "absolute", left, width: rect.width, top };
      }
      setLayout({ host, style: { ...style, maxHeight, transform: openUp ? "translateY(-100%)" : undefined } });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRef, open]);

  return layout;
}

export function ComboOptions({ anchorRef, open, options, active, onHover, onPick, selectedValue, emptyMessage = "No matches found.", listId, testIdPrefix }) {
  const layout = useAnchoredPosition(anchorRef, open);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open || active < 0 || !listRef.current) return;
    const list = listRef.current;
    const item = list.querySelector('[data-index="' + active + '"]');
    if (!item) return;
    // Scroll only the list (not the page) so the highlighted option stays visible.
    if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
    else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
  }, [open, active]);

  if (!open || typeof document === "undefined") return null;
  const host = layout?.host || document.body;
  const style = layout?.style || { position: "absolute", left: 0, top: 0, width: 240, maxHeight: 288 };

  return createPortal(
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      data-combo-options="true"
      style={{ ...style, zIndex: 80 }}
      className="overflow-y-auto rounded-md border border-border bg-popover p-1 text-sm text-popover-foreground shadow-lg"
      onMouseDown={(event) => event.preventDefault()}
    >
      {options.length === 0 && <li className="px-2 py-1.5 text-muted-foreground" role="presentation">{emptyMessage}</li>}
      {options.map((option, index) => {
        const selected = option.value === selectedValue;
        return (
          <li
            key={option.value + "-" + index}
            role="option"
            data-index={index}
            data-value={option.value}
            data-testid={testIdPrefix ? testIdPrefix + "-item-" + option.value : undefined}
            aria-selected={selected}
            aria-disabled={option.disabled || undefined}
            onMouseEnter={() => onHover?.(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              onPick(option);
            }}
            className={cn(
              "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5",
              index === active && "bg-accent/15",
              selected && "font-semibold",
              option.disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{option.label}</span>
            {option.hint && <span className="shrink-0 text-[11px] text-muted-foreground">{option.hint}</span>}
          </li>
        );
      })}
    </ul>,
    host
  );
}
